import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Actions from "../../components/Common/Actions";
import Breadcrumbs from "../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../components/Common/TabBar";
import ListPageLayout from "../../components/Layout";
import AssignedTo from "../../components/Modal/AssignedTo";
import Deletepopup from "../../components/Modal/Deletepopup";
import LeadStatus from "../../components/Modal/LeadStatus";
import SucessModal from "../../components/Modal/SucessModal";
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";

const AdLandingForms = () => {
  const navigate = useNavigate();
  const pathName = useLocation();

  const addNewLandingPage = {
    id: "addnewpost",
    onClick: () => navigate(),
    style: { width: "240px" },
    label: "Add New Landing Page",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }))
  );
  const [success, setSuccess] = useState(false);

  const [assignModal, setAssignModal] = useState({ id: "", status: false });
  const [leadStatusModal, setLeadStatusModal] = useState({
    id: "",
    status: false,
  });
  const [deleteModal, setDeleteModal] = useState({ id: "", status: false });

  // Delete state
  const [deleteId, setDeleteId] = useState("");

  const [showDelete, setShowDelete] = useState(false);
  const [leadStatussuccess, setLeadStatusSuccess] = useState(false);
  const pathname = useLocation().pathname;

  const [url, setUrl] = useState("");
  const [deleteUrl, setDeleteUrl] = useState("");

  useEffect(() => {
    if (pathname === "/ad-landing-forms/partnered-doctors") {
      setUrl("doctors-ad/list");
      setDeleteUrl("doctors-ad/doctors-ad-delete");
    } else if (pathname === "/ad-landing-forms/users-general") {
      setUrl("user-general/list");
      setDeleteUrl("user-general/user-general-delete");
    } else {
      setUrl("");
      setDeleteUrl("");
    }
  }, [pathname]);

  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}${url ? url : "doctors-ad/list"}`,
      initialParams: {
        sort,
        searchText: search,
      },
    });
  usePaginatedData({
    apiUrl: `${process.env.REACT_APP_API_URL}${url ? url : "doctors-ad/list"}`,
    initialParams: {},
  });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(deleteUrl, fetchData, setShowDelete);

  useEffect(() => {
    (!assignModal.status && !leadStatusModal.status) && setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.skip,
    assignModal,
    leadStatusModal.status,
    pagination.limit,
    sort,
    search,
    url,
    pathname,
  ]);

  const successModalClose = () => {
    setSuccess(false);
    setLeadStatusSuccess(false);
    fetchData();
  };

  const columns = useMemo(() => {
    const commonColumns = [
      {
        title: "S.No",
        render: (text, record, index) =>
          Number(pagination.skip || 0) + (index + 1),
        key: "serial",
        width: 80,
        fixed: "left",
      },
      { title: "Name", dataIndex: "name", key: "name", fixed: "left" },
      { title: "Email", dataIndex: "email", key: "email" },
      { title: "Mobile No", dataIndex: "mobile", key: "mobile" },
      { title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo" },
      { title: "Lead Status", dataIndex: "leadStatus", key: "leadStatus" },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Actions
            isLeadStatus={true}
            isEdit={true}
            isAssignedTo={true}
            isDelete={true}
            onClickEdit={() => {
              navigate(`/ad-landing-doctor-edit/${record.key}`);
            }}
            onClickDelete={() => {
              setDeleteId(record.key);
              setShowDelete(true);
            }}
            onClickAssignedTo={() =>
              setAssignModal({ id: record.key, status: true })
            }
            onClickLeadStatus={() =>
              setLeadStatusModal({ id: record.key, status: true })
            }
          />
        ),
      },
    ];
    if (pathName.pathname === "/ad-landing-forms/partnered-doctors") {
      return [
        ...commonColumns,
        { title: "Gender", dataIndex: "gender", key: "gender" },
        {
          title: "Primary Specialization",
          dataIndex: "specialization",
          key: "specialization",
        },
        {
          title: "Secondary Specialization",
          dataIndex: "secondarySpecialization",
          key: "secondarySpecialization",
        },
        {
          title: "Medical Registration No (MCI)",
          dataIndex: "mci",
          key: "mci",
        },
        {
          title: "Medical Council",
          dataIndex: "medicalCouncil",
          key: "medicalCouncil",
        },
        {
          title: "Year of Registration",
          dataIndex: "registeredYear",
          key: "registeredYear",
        },
        { title: "Experience", dataIndex: "experience", key: "experience" },
        { title: "Degree", dataIndex: "degree", key: "degree" },
        { title: "City", dataIndex: "city", key: "city" },
        { title: "Pincode", dataIndex: "pincode", key: "pincode" },
        {
          title: "In-Clinic Fee",
          dataIndex: "inClinicFee",
          key: "inClinicFee",
        },
        {
          title: "Online Consultation Fee",
          dataIndex: "onlineConsultationFee",
          key: "onlineConsultationFee",
        },
        { title: "Clinic Name", dataIndex: "clinicName", key: "clinicName" },
        {
          title: "Clinic Address",
          dataIndex: "clinicAddress",
          key: "clinicAddress",
          width: 300,
        },
        {
          title: "App Download Android Click",
          dataIndex: "androidClick",
          key: "androidClick",
        },
        {
          title: "App Download iOS Click",
          dataIndex: "iosClick",
          key: "iosClick",
        },
        {
          title: "Brochure Icon Click Status",
          dataIndex: "brochureClick",
          key: "brochureClick",
        },
      ];
    }
    if (pathName.pathname === "/ad-landing-forms/users-general") {
      return [
        ...commonColumns,
        { title: "Age", dataIndex: "age", key: "age" },
        {
          title: "Loaction",
          dataIndex: "location",
          key: "location",
        },
        {
          title: "App Download Android Click",
          dataIndex: "android_click",
          key: "android_click",
        },
        {
          title: "App Download iOS Click",
          dataIndex: "ios_click",
          key: "ios_click",
        },
        {
          title: "Brochure Icon Click Status (1.0)",
          dataIndex: "brochure_click",
          key: "brochure_click",
        },

        {
          title: "App Download Android Click",
          dataIndex: "androidClick",
          key: "androidClick",
        },
      ];
    }

    return commonColumns;
  }, [pathName]);

  const breadcrumbsList = [
    {
      text: "Ad Landing Forms",
      link: "ad-landing-forms/partnered-doctors",
    },
    {
      text:
        pathname === "/ad-landing-forms/partnered-doctors"
          ? "Partnered Doctors"
          : "Users General",
      link: "",
    },
  ];

  const adLandingFormsList = [
    {
      name: "Partnered Doctors",
      navigatePath: "/ad-landing-forms/partnered-doctors",
    },
    { name: "Users General", navigatePath: "/ad-landing-forms/users-general" },
  ];

  const dataSource = useMemo(() => {
    return records?.map((el, i) => ({
      key: el._id,
      serial: pagination.skip + i + 1,
      name: el.name || "-",
      mobile: el.mobile || "-",
      email: el.email || "-",
      assignedTo: el.assigned_to || "-",
      leadStatus: el.lead_status || "-",

      ...(pathname === "/ad-landing-forms/partnered-doctors" && {
        gender: el.gender || "-",
        specialization: el.specialization || "-",
        secondarySpecialization: el.secondary_specialization || "-",
        mci: el.mci || "-",
        medicalCouncil: el.medical_council || "-",
        registeredYear: el.registered_year || "-",
        experience: el.experience || "-",
        degree: el.degree || "-",
        city: el.city || "-",
        pincode: el.pincode || "-",
        inClinicFee: el.in_clinic_fee || "-",
        onlineConsultationFee: el.online_consultation_fee || "-",
        clinicName: el.clinic_name || "-",
        clinicAddress: el.clinic_address || "-",
        androidClick: el.android_click || "-",
        iosClick: el.ios_click || "-",
        brochureClick: el.brochure_click || "-",
      }),
      ...(pathname === "/ad-landing-forms/users-general" && {
        age: el.age || "-",
        location: el.location || "-",
        android_click: el.android_click || "-",
        ios_click: el.ios_click || "-",
        brochure_click: el.brochure_click || "-",
      }),
    }));
  }, [records, pathname, pagination.skip]);

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <>
      <ListPageLayout
        breadcrumbs={
          <Breadcrumbs
            className="healthsyMasterBreadCrumbs"
            breadcrumbsList={breadcrumbsList}
          />
        }
        addButton={<FilledButton props={addNewLandingPage} />}
        titles={<NavBarTitles titles={adLandingFormsList} />}
        filters={
          <DoctorsFilterOptions
            limit={pagination.limit}
            setLimit={(val) =>
              setPagination((prev) => ({
                ...prev,
                limit: val,
                skip: 0,
                currentPage: 1,
              }))
            }
            search={search}
            onChange={debouncedSearch}
            handleSortClick={handleSortClick}
            sortName={sortName}
            filter_records={() => {}}
            showFilter={false}
          />
        }
        tableProps={{
          columns,
          dataSource,
        }}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={(selected) => handlePageChange({ selected })}
        loader={loader}
      >
        {/* Optional children like modals */}
        {showDelete && (
          <Deletepopup
            handleDelete={() => handleDelete(deleteId)}
            id={deleteId}
            handleClose={() => setShowDelete(false)}
          />
        )}
      </ListPageLayout>
      {assignModal.status === true && (
        <AssignedTo
          id={assignModal?.id}
          setAssignModal={setAssignModal}
          setSuccess={setSuccess}
        />
      )}
      {leadStatusModal.status === true && (
        <LeadStatus
          id={leadStatusModal?.id}
          setLeadStatusModal={setLeadStatusModal}
          setLeadStatusSuccess={setLeadStatusSuccess}
        />
      )}

      {leadStatussuccess && (
        <SucessModal
          name="Lead Status"
          post="Updated"
          page={pathName.pathname}
          onClick={() => successModalClose()}
        />
      )}
    </>
  );
};

export default AdLandingForms;
